import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  InputAdornment,
  Tooltip,
  Alert,
  CircularProgress,
  Avatar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Search,
  FilterList,
  Visibility,
  Edit,
  Delete,
  Refresh,
  Add,
  VideoFile,
  Image,
  ExpandMore,
  CloudUpload,
  PlayArrow,
  ImageOutlined,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
import {
  fetchMedia,
  deleteMedia,
  setFilters,
  clearFilters,
  setPage,
  fetchMediaStats,
} from '../store/slices/mediaSlice';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { MediaData } from '../types';
import MediaFormModal from '../components/media/MediaFormModal';
import MediaDetailsModal from '../components/media/MediaDetailsModal';

const MediaPage: React.FC = () => {
  const dispatch = useDispatch();
  const { media, pagination, statistics, loading, error } = useSelector(
    (state: RootState) => state.media
  );
  
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [isVideoFilter, setIsVideoFilter] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<MediaData | null>(null);
  const [showMediaDetails, setShowMediaDetails] = useState(false);
  const [showMediaForm, setShowMediaForm] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchMedia({ page: 1, limit: 20 }) as any);
    dispatch(fetchMediaStats() as any);
  }, [dispatch]);

  const handleSearch = () => {
    const filters: any = {};
    if (searchTerm) filters.search = searchTerm;
    if (typeFilter) filters.type = typeFilter;
    if (isVideoFilter !== '') filters.isVideo = isVideoFilter === 'true';
    
    dispatch(setFilters(filters));
    dispatch(fetchMedia({ page: 1, limit: 20, filters }) as any);
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    dispatch(setPage(newPage + 1));
    dispatch(fetchMedia({ page: newPage + 1, limit: 20 }) as any);
  };

  const handleViewMedia = (media: MediaData) => {
    setSelectedMedia(media);
    setShowMediaDetails(true);
  };

  const handleEditMedia = (media: MediaData) => {
    setSelectedMedia(media);
    setShowMediaForm(true);
  };

  const handleDeleteMedia = async (mediaId: string) => {
    if (window.confirm('Are you sure you want to delete this media?')) {
      setActionLoading(mediaId);
      try {
        const result = await dispatch(deleteMedia(mediaId) as any);
        if (result.payload?.success) {
          toast.success('Media deleted successfully');
          dispatch(fetchMedia({ page: pagination.page, limit: 20 }) as any);
        } else {
          toast.error('Failed to delete media');
        }
      } catch (error) {
        toast.error('An error occurred while deleting media');
      } finally {
        setActionLoading(null);
      }
    }
  };

  const handleClearFilters = () => {
    dispatch(clearFilters());
    dispatch(fetchMedia({ page: 1, limit: 20 }) as any);
    setSearchTerm('');
    setTypeFilter('');
    setIsVideoFilter('');
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'creation': return 'primary';
      case 'starting': return 'success';
      case 'ending': return 'error';
      default: return 'default';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'creation': return <CloudUpload />;
      case 'starting': return <PlayArrow />;
      case 'ending': return <ImageOutlined />;
      default: return <Image />;
    }
  };

  const getFileIcon = (isVideo: boolean) => {
    return isVideo ? <VideoFile /> : <Image />;
  };

  const mediaTypes = [
    { value: 'creation', label: 'Creation' },
    { value: 'starting', label: 'Starting' },
    { value: 'ending', label: 'Ending' },
  ];

  const videoTypes = [
    { value: 'true', label: 'Video' },
    { value: 'false', label: 'Image' },
  ];

  if (loading && media.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: { xs: 1, sm: 3 } }}>
        Media Management
      </Typography>

      {/* Statistics Cards */}
      {statistics && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 3 } }}>
          <Card sx={{ flex: '1 1 140px', minWidth: 140 }}>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                  <Image />
                </Avatar>
                <Box>
                  <Typography variant="h6">{statistics.totalMedia}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Media
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
          <Card sx={{ flex: '1 1 140px', minWidth: 140 }}>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>
                  <VideoFile />
                </Avatar>
                <Box>
                  <Typography variant="h6">{statistics.totalVideos}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Videos
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
          <Card sx={{ flex: '1 1 140px', minWidth: 140 }}>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Avatar sx={{ bgcolor: 'info.main', mr: 2 }}>
                  <Image />
                </Avatar>
                <Box>
                  <Typography variant="h6">{statistics.totalImages}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Images
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
          <Card sx={{ flex: '1 1 140px', minWidth: 140 }}>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Avatar sx={{ bgcolor: 'warning.main', mr: 2 }}>
                  <CloudUpload />
                </Avatar>
                <Box>
                  <Typography variant="h6">{statistics.recentUploads}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Recent Uploads
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Search and Filters */}
      <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'center' } }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, flex: 1 }}>
            <TextField
              placeholder="Search media..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: { xs: '100%', sm: 200 } }}
              size="small"
            />
            <FormControl sx={{ minWidth: { xs: '100%', sm: 120 } }} size="small">
              <InputLabel>Type</InputLabel>
              <Select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                label="Type"
              >
                <MenuItem value="">All Types</MenuItem>
                {mediaTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: { xs: '100%', sm: 120 } }} size="small">
              <InputLabel>Format</InputLabel>
              <Select
                value={isVideoFilter}
                onChange={(e) => setIsVideoFilter(e.target.value)}
                label="Format"
              >
                <MenuItem value="">All Formats</MenuItem>
                {videoTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' }, 
            gap: 1, 
            justifyContent: { xs: 'stretch', sm: 'flex-end' },
            width: { xs: '100%', sm: 'auto' }
          }}>
            {/* First row of buttons */}
            <Box sx={{ 
              display: 'flex', 
              gap: 1, 
              flexDirection: { xs: 'row', sm: 'row' },
              width: { xs: '100%', sm: 'auto' }
            }}>
              <Button variant="contained" onClick={handleSearch} size="small" sx={{ flex: { xs: 1, sm: 'none' } }}>
                Search
              </Button>
              <Button variant="outlined" onClick={handleClearFilters} size="small" sx={{ flex: { xs: 1, sm: 'none' } }}>
                Clear
              </Button>
            </Box>
            
            {/* Second row of buttons */}
            <Box sx={{ 
              display: 'flex', 
              gap: 1, 
              flexDirection: { xs: 'row', sm: 'row' },
              width: { xs: '100%', sm: 'auto' }
            }}>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setShowMediaForm(true)}
                size="small"
                sx={{ flex: { xs: 1, sm: 'none' } }}
              >
                Upload
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Media Table */}
      <Paper>
        <TableContainer sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <Table sx={{ minWidth: { xs: 400, sm: 600 } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Preview</TableCell>
                <TableCell>Type</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Format</TableCell>
                <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>File URL</TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {media.map((mediaItem) => (
                <TableRow key={mediaItem.id}>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    <Avatar sx={{ width: 40, height: 40 }}>
                      {getFileIcon(mediaItem.isVideo)}
                    </Avatar>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Chip
                        icon={getTypeIcon(mediaItem.type)}
                        label={mediaItem.type}
                        color={getTypeColor(mediaItem.type) as any}
                        size="small"
                      />
                      <Chip
                        icon={getFileIcon(mediaItem.isVideo)}
                        label={mediaItem.isVideo ? 'Video' : 'Image'}
                        color={mediaItem.isVideo ? 'primary' : 'secondary'}
                        size="small"
                        sx={{ display: { xs: 'inline-flex', md: 'none' } }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                    <Chip
                      icon={getFileIcon(mediaItem.isVideo)}
                      label={mediaItem.isVideo ? 'Video' : 'Image'}
                      color={mediaItem.isVideo ? 'primary' : 'secondary'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                    <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {mediaItem.fileUrl}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    <Typography variant="body2">
                      {dayjs(mediaItem.createdAt).format('MMM DD, YYYY HH:mm')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5} flexWrap="wrap">
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleViewMedia(mediaItem)}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleEditMedia(mediaItem)}
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteMedia(mediaItem.id)}
                          disabled={actionLoading === mediaItem.id}
                        >
                          {actionLoading === mediaItem.id ? <CircularProgress size={16} /> : <Delete />}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={pagination.total}
          page={pagination.page - 1}
          onPageChange={handlePageChange}
          rowsPerPage={pagination.limit}
          rowsPerPageOptions={[10, 20, 50]}
          sx={{
            '& .MuiTablePagination-toolbar': {
              flexDirection: 'row',
              gap: { xs: 0.5, sm: 1 },
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
            },
            '& .MuiTablePagination-spacer': {
              display: 'none',
            },
            '& .MuiTablePagination-selectLabel': {
              display: 'none',
            },
            '& .MuiTablePagination-displayedRows': {
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              order: { xs: 2, sm: 0 },
            },
            '& .MuiTablePagination-select': {
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              order: { xs: 1, sm: 0 },
            },
            '& .MuiTablePagination-actions': {
              order: { xs: 3, sm: 0 },
            },
          }}
        />
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {/* Media Form Modal */}
      <MediaFormModal
        open={showMediaForm}
        onClose={() => setShowMediaForm(false)}
        onSuccess={() => {
          setShowMediaForm(false);
          dispatch(fetchMedia({ page: pagination.page, limit: 20 }) as any);
        }}
        media={selectedMedia}
      />

      {/* Media Details Modal */}
      <MediaDetailsModal
        open={showMediaDetails}
        onClose={() => setShowMediaDetails(false)}
        media={selectedMedia}
      />
    </Box>
  );
};

export default MediaPage;
